import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { JWT_TOKEN } from '../storagekeys'; // If you need this for any purpose
import { cacheKeys } from './cacheKeys';
import { ENV_TYPES } from '../components/global/Sidebar';
import { config } from './apiUrlConfig';
import { authApiSlice } from './user/authentication/authApiSlice';
import { authMutex as mutex } from './authMutex';
import { refreshTokenAndRetry } from './authSync';
const baseQuery = fetchBaseQuery({
    baseUrl: config.url.API_BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
        // console.log('[ApiSlice:prepareHeaders] Preparing headers for request');
        const token = getState().auth.token;

        if (token) {
            const tokenStart = token.substring(0, 10);
            const tokenEnd = token.substring(token.length - 10);
            // console.log(`[ApiSlice:prepareHeaders] Setting auth token: ${tokenStart}...${tokenEnd}`);
            headers.set('authorization', `Bearer ${token}`);
        } else {
            //console.warn('[ApiSlice:prepareHeaders] No auth token available');
        }

        const language = getState().settings.language;
        if (language) {
            // console.log(`[ApiSlice:prepareHeaders] Setting language: ${language}`);
            headers.set('Accept-Language', language);
        }

        const warehouseId = getState().warehouse.activeId;
        const type = getState().settings.environment.selectedEnvironmentType;
        const id = getState().settings.environment.selectedId;

        if (warehouseId && !headers.has('warehouse_id')) {
            //console.log(`[ApiSlice:prepareHeaders] Setting warehouse_id: ${warehouseId}`);
            headers.set('warehouse_id', warehouseId);
        }

        if (type === ENV_TYPES[100]) {
            // CUSTOMER
            //console.log(`[ApiSlice:prepareHeaders] Environment: CUSTOMER, id: ${id}`);
            headers.set("customer_id", id);
        } else if (type === ENV_TYPES[200]) {
            // RELATION
            //console.log(`[ApiSlice:prepareHeaders] Environment: RELATION, id: ${id}`);
            headers.set("relation_id", id);
        } else {
            //console.log(`[ApiSlice:prepareHeaders] Environment type: ${type}, id: ${id}`);
        }

        //console.log('[ApiSlice:prepareHeaders] Headers prepared successfully');
        return headers;
    },
});

// Custom base query with re-authentication logic
const baseQueryWithReauth = async (args, api, extraOptions) => {
    const requestUrl = typeof args === 'string' ? args : args?.url;
    // console.log(`[ApiSlice:reauth] Starting request to: ${requestUrl}, endpoint: ${api.endpoint}`);
    // console.log(`[ApiSlice:reauth] Mutex locked: ${mutex.isLocked()}`);

    // First, try the base query
    await mutex.waitForUnlock();
    // console.log(`[ApiSlice:reauth] Mutex unlocked, executing base query for: ${requestUrl}`);

    // The token this request is about to be sent with. refreshTokenAndRetry
    // compares it against the store, so a refresh that landed while we were
    // waiting for the response — ours or a sibling tab's — is adopted instead of
    // spending our rotated refresh token on a second one.
    const staleToken = api.getState().auth.token;

    let result = await baseQuery(args, api, extraOptions);
    // console.log(`[ApiSlice:reauth] Base query result for ${requestUrl} - status: ${result.error?.status ?? 'OK'}, hasData: ${!!result.data}, hasError: ${!!result.error}`);

    if (result.error) {
        // console.warn(`[ApiSlice:reauth] Request error for ${requestUrl}:`, JSON.stringify(result.error));
    }

    // Check for 401 Unauthorized status
    if (result.error && result.error.status === 401) {
        // console.log(`[ApiSlice:reauth] 401 received for ${requestUrl} (endpoint: ${api.endpoint}), attempting token refresh`);
        if (!mutex.isLocked()) {
            // console.log(`[ApiSlice:reauth] Acquiring mutex for token refresh`);
            const release = await mutex.acquire();
            // console.log(`[ApiSlice:reauth] Mutex acquired, starting token refresh`);
            try{
                // console.log(`[ApiSlice:reauth] Dispatching refresh token request`);
                result = await refreshTokenAndRetry({
                    api,
                    staleToken,
                    refresh: (refreshToken) => api.dispatch(authApiSlice.endpoints.refreshToken.initiate({ "refresh_token": refreshToken })),
                    retry: () => baseQuery(args, api, extraOptions),
                    failedResult: result,
                });
                // console.log(`[ApiSlice:reauth] Reauth result for ${requestUrl} - status: ${result.error?.status ?? 'OK'}, hasData: ${!!result.data}`);
            } finally {
                // release must be called once the mutex should be released again.
                // console.log(`[ApiSlice:reauth] Releasing mutex`);
                release()
            }
        } else {
            // wait until the mutex is available without locking it
            // console.log(`[ApiSlice:reauth] Mutex already locked, waiting for unlock before retrying ${requestUrl}`);
            await mutex.waitForUnlock()
            // console.log(`[ApiSlice:reauth] Mutex unlocked, retrying request to: ${requestUrl}`);
            result = await baseQuery(args, api, extraOptions)
            // console.log(`[ApiSlice:reauth] Retry after wait result for ${requestUrl} - status: ${result.error?.status ?? 'OK'}, hasData: ${!!result.data}`);
        }
    }

    // console.log(`[ApiSlice:reauth] Returning result for ${requestUrl} - success: ${!result.error}`);
    return result;
};

// Create the API slice using the custom base query
export const apiSlice = createApi({
    reducerPath: "api",
    baseQuery: baseQueryWithReauth,
    tagTypes: cacheKeys,
    refetchOnMountOrArgChange: true,
    endpoints: (builder) => ({
        // Define your endpoints here
    }),
});

// Export hooks for usage in functional components
// export const { useGetRandQuery } = apiSlice; // Uncomment and adjust as necessary
